import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listarEmpresas,
  obtenerEmpresa,
} from "../../../../services/empresasService";
import { normalizeEmpresa } from "../adapters/empresasAdapter";
import { showToast } from "../../../../utils/showToast";

const initialFilters = { search: "", activo: "all", tipo: "all" };

export default function useEmpresasList() {
  const [empresas, setEmpresas] = useState([]);
  const [seleccionada, setSeleccionada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchEmpresas = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarEmpresas();
      setEmpresas((Array.isArray(data) ? data : []).map(normalizeEmpresa));
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo obtener las empresas.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  const resumen = useMemo(() => {
    const total = empresas.length;
    const activas = empresas.filter((e) => e.activo).length;
    const inactivas = total - activas;
    const internas = empresas.filter((e) => e.tipo === "interna").length;
    const externas = empresas.filter((e) => e.tipo === "externa").length;
    const proveedores = empresas.filter((e) => e.tipo === "proveedor").length;
    return { total, activas, inactivas, internas, externas, proveedores };
  }, [empresas]);

  const empresasFiltradas = useMemo(() => {
    return empresas.filter((emp) => {
      if (filtros.activo === "1" && !emp.activo) return false;
      if (filtros.activo === "0" && emp.activo) return false;
      if (filtros.tipo !== "all" && filtros.tipo !== "" && emp.tipo !== filtros.tipo) return false;
      if (filtros.search) {
        const term = filtros.search.toLowerCase();
        const nombre = (emp.nombre || "").toLowerCase();
        const ruc = (emp.ruc || "").toLowerCase();
        const email = (emp.email || "").toLowerCase();
        const telefono = (emp.telefono || "").toLowerCase();
        const tipo = (emp.tipo || "").toLowerCase();
        if (!nombre.includes(term) && !ruc.includes(term) && !email.includes(term) && !telefono.includes(term) && !tipo.includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [empresas, filtros]);

  const total = empresasFiltradas.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filtros, total]);

  const empresasPaginadas = useMemo(() => {
    const start = (page - 1) * pageSize;
    return empresasFiltradas.slice(start, start + pageSize);
  }, [empresasFiltradas, page, pageSize]);

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const upsertEmpresa = useCallback((empresa) => {
    if (!empresa?.id) return;
    setEmpresas((prev) => {
      const exists = prev.some((item) => String(item.id) === String(empresa.id));
      if (exists) {
        return prev.map((item) =>
          String(item.id) === String(empresa.id) ? normalizeEmpresa({ ...item, ...empresa }) : item
        );
      }
      return [normalizeEmpresa(empresa), ...prev];
    });
    setLastUpdated(new Date());
  }, []);

  const removeEmpresa = useCallback((id) => {
    setEmpresas((prev) => prev.filter((item) => String(item.id) !== String(id)));
    setLastUpdated(new Date());
  }, []);

  const seleccionar = useCallback(async (empresa) => {
    if (!empresa) {
      setSeleccionada(null);
      return;
    }
    const id = empresa.id ?? empresa;
    if (typeof empresa === "object") setSeleccionada(normalizeEmpresa(empresa, id));
    try {
      const detalle = await obtenerEmpresa(id);
      setSeleccionada(normalizeEmpresa(detalle, id));
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: "No se pudo cargar la empresa seleccionada." });
    }
  }, []);

  const limpiarFiltros = useCallback(() => setFiltros(initialFilters), []);

  return {
    empresas,
    empresasFiltradas,
    empresasPaginadas,
    seleccionado: seleccionada,
    setSeleccionado: setSeleccionada,
    loading,
    error,
    resumen,
    filtros,
    setFiltros,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    reload: fetchEmpresas,
    seleccionar,
    upsertEmpresa,
    removeEmpresa,
  };
}

export { initialFilters as initialEmpresaFilters };
